import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import InternalDashboard from './pages/InternalDashboard';
import ClientDashboard from './pages/ClientDashboard';
import ProjectDetail from './pages/ProjectDetail';
import { Toaster } from './components/ui/sonner';
import { setToken, clearToken, getToken, apiGet } from './lib/api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If returning from OAuth callback, don't try /me — AuthCallback will handle auth
    if (window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }

    if (getToken()) {
      checkSession();
    } else {
      setLoading(false);
    }

    const handleExpired = () => {
      clearToken();
      setUser(null);
    };
    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, []);

  const checkSession = async () => {
    try {
      const res = await apiGet('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        clearToken();
      }
    } catch (error) {
      clearToken();
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, userData) => {
    setToken(token);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await apiGet('/api/auth/logout');
    } catch (e) {
      // ignore
    }
    clearToken();
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-page">
        <div className="glass p-8 rounded-2xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Router>
        <Routes>
          <Route
            path="/login"
            element={
              user ? (
                user.role === 'Client' ? <Navigate to="/client" /> : <Navigate to="/internal" />
              ) : (
                <LoginPage />
              )
            }
          />

          <Route
            path="/auth/callback"
            element={<AuthCallback onLogin={handleLogin} />}
          />

          <Route
            path="/internal/*"
            element={
              user && user.role !== 'Client' ? (
                <InternalDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route
            path="/client/*"
            element={
              user && user.role === 'Client' ? (
                <ClientDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route
            path="/project/:projectId"
            element={
              user ? (
                <ProjectDetail user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route
            path="/"
            element={
              user ? (
                user.role === 'Client' ? <Navigate to="/client" /> : <Navigate to="/internal" />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </Router>
      <Toaster position="top-right" />
    </>
  );
}

export default App;
